/**
 * Transforms a booking response into a booking object
 *
 * @param response The API response
 * @returns The booking object
 */
export function transformBooking(response: ApiSchema['BookingResponse']): Booking {
    const booking = response.booking;

    const allBookingParts = booking?.bookedOffers
        ?.flatMap(bo => [
            ...(bo.activities || []),
            ...(bo.admissions || []),
            ...(bo.reservations || []),
            ...(bo.accommodations || []),
            ...(bo.ancillaries || []),
        ]) ?? [];

    const hasBookingPartsFulfilled = allBookingParts.some(op => op.status === 'FULFILLED' || op.status === 'CONFIRMED');
    const hasBookingPartsCancelled = allBookingParts.some(op => op.status === 'CANCELLED');
    const hasBookingPartsErrored = allBookingParts.some(op => op.status === 'ERROR');

    let status: Booking['status'] = 'PENDING';

    if (hasBookingPartsFulfilled) {
        status = 'FULFILLED';
    }
    else if (hasBookingPartsCancelled) {
        status = 'CANCELLED';
    }
    else if (hasBookingPartsErrored) {
        status = 'ERROR';
    }

    return {
        id: booking?.id ?? '',
        bookingNo: booking?.bookingNo ?? '',
        openAmount: booking?.openAmount ?? { amount: 0, currency: 'CHF' },
        confirmedPrice: booking?.confirmedPrice ?? { amount: 0, currency: 'CHF' },
        purchaser: transformPurchaser(booking?.purchaser),
        confirmationTimeLimit: zurichDate(booking?.confirmationTimeLimit),
        orderDate: zurichDate(booking?.createdOn),
        paymentMethods: extractPaymentMethods(booking),
        remark: booking?.customerRemark ?? '',
        status,
    };
}

function extractPaymentMethods(booking?: ApiSchema['Booking']): Booking['paymentMethods'] {
    return booking?.paymentMethods
        ?.filter(pm => pm.originalPaymentId === null && pm.state === 'SETTLED')
        .map(pm => ({
            paymentMethod: pm.paymentMethod,
            voucherInformation: pm.voucherInformation,
        })) ?? [];
}
